<template>
  <IssueLocationTemplate
    v-if="currentOperation"
    ref="template"
    v-model:region-data="regions"
    :backUrl="backUrl"
    :data="currentOperation"
    :disableSave="disableSave"
    :isHme="isHme"
    :isOutOfBounds="isOutOfBounds"
    :loading="loadingOperation"
    operationType="plants"
    :saving="saving"
    :textData="textData"
    @map-click="onMapClick"
    @map-init="onMapInit"
    @save="storeIssueLocation"
    @update:toolbar-state="selectedAction"
  />
</template>

<script setup>
import { computed, ref, reactive } from 'vue';
import I18n from 'i18n-js';

// Utils
import useAjax from '@/functions/use_ajax';
import { useRoute, useRouter } from 'vue-router';
import { useOperation } from '@/functions/use_remote_object';
import ancestry from '@/functions/ancestry';
import generateTextData from '@/functions/generate_text_data';
import { screenAtLeast } from '@/functions/screen_width';
import regionPolygonOptions from '@/functions/region_polygon_options';
import { useIssueLocationStore } from '@/stores/shafts/issueLocationStore';
import { useIssuesStore } from '@/stores/issues/issuesStore';
import pointInPolygon from '@turf/boolean-point-in-polygon';
import {
  latLngToGeoJSON,
  latLngToRgeo,
  rgeoToLatLng,
  geoJSONPolygonToLatLngs,
  rgeoPolygonToGeoJSON,
} from '@/functions/geospatial_conversions';

// Components
import IssueLocationTemplate from '@/components/issues/issue_location_template.vue';

// Svg
import { DotIcon } from '@/vendor/leaflet_mods';

const PointIcon = DotIcon.extend({
  options: {
    className: 'map-modal__point',
    iconSize: [9, 9],
    iconAnchor: [5, 5],
  },
});

const props = defineProps({
  organisationId: { type: String, required: true },
  operationId: { type: String, required: true },
  inspectionId: { type: String, required: false, default: null },
  issueType: { type: String, required: false, default: null },
  issueId: { type: String, required: false, default: null },
  /** Use a specific route for the header nav back button */
  issueNavBackRoute: { type: String, required: false, default: null },
});

const issueStore = useIssuesStore();
const issueLocationStore = useIssueLocationStore();
const template = ref(null);
const route = useRoute();
const router = useRouter();
const ajax = useAjax();
const currentOperation = useOperation({
  organisationId: props.organisationId,
  operationId: props.operationId,
});

const saving = ref(false);
const loadingOperation = ref(false);
const issueLocation = ref(null);
const regions = ref({});
const regionMap = ref(null);
const regionTree = ref([]);
const location = ref(null);
const currentValue = ref(null);
let marker = null;
const isHme = ref([
  'issueLocationHme',
  'issueLocationHmes',
  'regionsLocationTypeHme',
  'newIssueLocationHmes'
].includes(route?.name));
const isOutOfBounds = ref(false);
const mainKey = 'shafts.group.heavy_mobile_equipment';
const diagramArray = [
  'diagram_loading_title',
  'diagram_loading_description'
];
const noHmeArray = [
  'title',
  'message'
];
const textData = {
  cancel: I18n.t('general.cancel'),
  diagram: {
    ...generateTextData(mainKey, diagramArray)
  },
  noHme: {
    ...generateTextData(`${mainKey}.no_hme_selection`, noHmeArray)
  }
};

/**
 * Disable the save button if the location is not set
 * and we some how make it to the HME set location page though HME is disabled
 */
const disableSave = computed(() => {
  return !isHme.value ? !issueLocation.value : !issueLocation.value || !currentOperation.value?.policy?.heavy_mobile_equipment_enabled;
});

// Checks if a specific route was passed in via props
const hasIssueNavBackRoute = computed(() => {
  return props.issueNavBackRoute !== null;
});

// Default back route - to inspections
const defaultNavBackRoute = computed(() => {
  if (!route.params.issueId || route.params.issueId === 'null' || route.params.issueId === 'undefined') return { name: 'newIssue' };
  return { name: 'issue', params: { issueId: route.params.issueId }};
});

// Set back route based on props
const backUrl = computed(() => {
  return hasIssueNavBackRoute.value ? { name: props.issueNavBackRoute } : defaultNavBackRoute.value;
});

async function onMapInit() {
  await loadRegions();
  loadLocation();
}

function loadLocation() {
  if (issueLocationStore?.location?.location) {
    let location = rgeoToLatLng(issueLocationStore.location.location);
    setMarker(...location);
  }
}

async function loadRegions() {
  let { ok, data } = await ajax(`/organisations/${props.organisationId}/operations/${props.operationId}/regions`);

  if (ok) {
    regions.value = data.regions;
    regionMap.value = new Map();
    if (!template.value?.map) return; // in case user navigates away

    regions.value.forEach(loadRegion);
    regionTree.value = ancestry(regionMap.value.values());
  }
}

function loadRegion(region) {
  let geoJSON = rgeoPolygonToGeoJSON(region.bounds);
  let latLngs = geoJSONPolygonToLatLngs(geoJSON);
  let polygon = template.value?.map.addPolygon(latLngs, regionPolygonOptions(region.region_type));
  let regionObject = {
    ...region,
    geoJSON,
    latLngs,
    polygon,
  };
  polygon.disableEdit();
  polygon.region = regionObject;

  regionMap.value.set(region.id, reactive(regionObject));
}

function onMapClick({ latlng: { lat, lng } }) {
  location.value = null;
  issueLocation.value = true;
  if (screenAtLeast('medium')) {
    setMarker(lat, lng);
  }
}

function setMarker(lat, lng) {
  template.value?.map.enableCrosshair(lat, lng);
  if (!marker) {
    marker = template.value?.map.addMarker([lat, lng], { icon: new PointIcon() });
  } else {
    marker.setLatLng([lat, lng]);
  }
  isOutOfBounds.value = false;
  currentValue.value = [lat, lng];
}

function storeIssueLocation(issueId) {
  if (validateIssuePoint()) return;

  const newPoint = currentValue.value ? latLngToRgeo(currentValue.value) : null;

  if (!!issueId && issueId !== 'null' && issueId !== 'undefined') {
    updateActivity({
      issue: {
        location_type: 'Plants::PlantLocation',
        location_attributes: {
          id: issueLocationStore.formData?.location.id,
          location: newPoint,
          operation_id: props.operationId
        },
      },
    });
  } else {
    issueLocationStore.$patch({ location: { type: 'plant', location: newPoint } });

    router.push(backUrl.value);
  }
}

/**
 *
 * Save the updated location attributes
 */
async function updateActivity(attributes) {
  if (validateIssuePoint()) return;

  saving.value = true;

  if (!route.params.issueId || route.params.issueId === 'null') return router.push(backUrl.value);

  const baseUrl = `/organisations/${props.organisationId}/operations/${props.operationId}`;
  const inspectionUrl = `${baseUrl}/plants/inspections/${props.inspectionId}`;
  const issueUrl = `${inspectionUrl}/issues/${route.params.issueId}`;
  let { ok, data } = await ajax(issueUrl, {
    method: 'PATCH',
    body: attributes,
  });

  if (ok) {
    issueStore.updateIssue(data);
    router.push(backUrl.value);
  }
  saving.value = false;
}

/**
 * Gets the selected toolbar option and
 * calls the relevant method to update the view
 */
function selectedAction(event) {
  if (event === 'reset') {
    if (currentValue.value && marker) return template.value?.map.goTo(currentValue.value, 18);
    template.value?.map?.resetZoom();
  }
  // TODO: undo and redo
  // if (event === 'undo') return;
  // if (event === 'redo') return;
}

function validateIssuePoint() {
  let point = latLngToGeoJSON(currentValue.value);
  let polygon = rgeoPolygonToGeoJSON(currentOperation.value.bounds);
  isOutOfBounds.value = !pointInPolygon(point, polygon);
  return isOutOfBounds.value;
}
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';
.location-row {
  &:not(:last-of-type) {
    margin-bottom: bu(3.75);
  }

  p {
    margin: 0 0 bu(2) 0;
    line-height: bu(2.5);

    &:last-child {
      margin-top: bu(2);
    }
  }
}
.map-container {
  height: 100%;
  overflow: hidden;
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
}
</style>
